import {
  Grid,
  GridItem,
  Header,
  ProductHub,
  BuilderFrameIconButton,
  SideBar,
  SideBarItem,
  BuilderFrameButton,
  ProductListButton,
  HeaderLayout,
} from "@zapier/product-nav";
import {
  SUMMARY,
  SideBarSelected,
  showCanvasTemplateWidgetAtom,
  isPublicAtom,
  isViewOnlyModeAtom,
  sessionAtom,
  sideBarSelectedAtom,
  togglesAtomResettable,
  sidebarOpenNodeIdAtom,
  isViewerAtom,
  hasZslAtom,
  showZslFeedbackAtom,
  showAIStreamCreateModalAtom,
  isTemplatePageAtom,
  canvasAtom,
  isViewOnlyDowntimeOverrideAtom,
  hasStreamingProgressStartedAtom,
  singleSelectedNodeTypeAtom,
  zslHasDraftNodesAtom,
  isAiPageAtom,
} from "@zapier/canvas-state";
import {
  showWelcomeModalAtom,
  AIStreamCreateModal,
  isModifyPromptOpenAtom,
} from "@zapier/streaming";
import { useAtom, useAtomValue } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { Suspense, useCallback } from "react";
import { Spacer } from "@zapier/design-system";

import { InteractiveName } from "../InteractiveName";
import ShareButton, { LoadingShareButton } from "../ShareButton/ShareButton";
import { ZoomSelect } from "../ZoomSelect";
import { HelpMenu } from "../HelpMenu";
import { TemplateBanner } from "../TemplateBanner";
import { ProjectSideBarItem } from "../ProjectSideBarItem";
import { SidebarDrawer } from "../SidebarDrawer";
import capitalizeFirstLetter from "../../utils/capitalizeFirstLetter";
import { DiagrammingToolbar } from "../DiagrammingToolbar";
import { CreateCanvasFromTemplateToolbar } from "../CreateCanvasFromTemplateToolbar";
import { AiCopilotBanner } from "../AiCopilotBanner";
import { TemplateFeedback } from "../TemplateFeedback";
import { LoggedOutTemplateBanner } from "./LoggedOutTemplateBanner";
import { TemplatePageBanner } from "../TemplatePageBanner";
import { ProgressBanner } from "../ProgressBanner";

import { ViewOnlyDowntimeBanner } from "./ViewOnlyDowntimeBanner";
import { DraftBanner } from "../DraftBanner";

type Props = {
  children: React.ReactNode;
};

// TODO: Import this from @zapier/product-nav when possible
type HeaderItemProps = {
  component: React.ReactElement | null;
  hideOnMobile?: boolean;
  hideOnTablet?: boolean;
  showOnTablet?: boolean;
  showOnMobile?: boolean;
};

export function BuilderNav({ children }: Props) {
  const canvas = useAtomValue(canvasAtom);
  const hasZsl = useAtomValue(hasZslAtom);
  const sideBarSelected = useAtomValue(sideBarSelectedAtom);
  const toggles = useAtomValue(togglesAtomResettable);
  const session = useAtomValue(sessionAtom);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const isPublic = useAtomValue(isPublicAtom);
  const showCanvasTemplateWidget = useAtomValue(showCanvasTemplateWidgetAtom);
  const showWelcomeModal = useAtomValue(showWelcomeModalAtom);
  const showAIStreamCreateModal = useAtomValue(showAIStreamCreateModalAtom);
  const [showTemplateFeedback, setShowTemplateFeedback] =
    useAtom(showZslFeedbackAtom);
  const zslHasDraftNodes = useAtomValue(zslHasDraftNodesAtom);
  const singleSelectedNodeType = useAtomValue(singleSelectedNodeTypeAtom);
  const sidebarOpenNodeId = useAtomValue(sidebarOpenNodeIdAtom);
  const showDiagrammingToolbar =
    !isViewOnlyMode && !sidebarOpenNodeId && singleSelectedNodeType !== "path"; // Because path nodes don't have floating toolbar (when path sidebar is open, don't show diagramming toolbar)
  const isAiPage = useAtomValue(isAiPageAtom);
  const handleOnClose = useAtomCallback(
    useCallback((get, set) => {
      const isModifyPromptOpen = get(isModifyPromptOpenAtom);
      if (isModifyPromptOpen) {
        set(isModifyPromptOpenAtom, false);
      } else {
        set(sideBarSelectedAtom, null);
      }
    }, []),
  );

  const handleSideBarItemPress = useAtomCallback(
    useCallback(
      (get, set, selectedItem: SideBarSelected) => {
        const sideBarSelected = get(sideBarSelectedAtom);
        if (sideBarSelected === selectedItem) {
          handleOnClose();
        } else {
          set(sideBarSelectedAtom, selectedItem);
        }
      },
      [handleOnClose],
    ),
  );

  const rightColumnComponents: HeaderItemProps[] = [
    !hasZsl &&
      !isPublic && {
        component: (
          <Suspense fallback={<LoadingShareButton />}>
            <ShareButton />
            <Spacer width={5} />
          </Suspense>
        ),
        hideOnTablet: true,
      },
    session?.is_logged_in &&
      session?.current_account_id === canvas.account_id &&
      isPublic && {
        component: (
          <BuilderFrameButton href={`/app/canvas/${canvas.id}`}>
            Edit
          </BuilderFrameButton>
        ),
      },
    !session?.is_logged_in &&
      isPublic && {
        component: (
          <BuilderFrameButton href={`/app/login?next=/app/canvas/${canvas.id}`}>
            Log in to edit
          </BuilderFrameButton>
        ),
        hideOnTablet: true,
      },
    {
      component: <ZoomSelect />,
      hideOnTablet: true,
    },
    {
      component: <HelpMenu />,
      hideOnTablet: false,
    },
  ].filter(Boolean) as HeaderItemProps[];

  return (
    <Grid columns="44px 1fr" rows="44px 1fr" height="inherit">
      <GridItem overrideStyles={{ gridColumn: "1 / -1" }}>
        <Header>
          <HeaderLayout
            leftColumnComponents={[
              {
                component: (
                  <BuilderFrameIconButton
                    icon="navHome"
                    label="Home"
                    href="/"
                    aria-label="Home"
                  />
                ),
              },
              { component: <Spacer width={15} />, hideOnMobile: true },
              {
                component: (
                  <ProductHub
                    productName="Canvas"
                    // TODO: Add this footer in later when we get copy requirements
                    // footer={
                    //   !account?.plan_info.allow_companies ? (
                    //     <>
                    //       You are on the{" "}
                    //       <strong>{account?.plan_info.name}</strong> plan.
                    //       Automate at scale with our <a href="/">Company</a>{" "}
                    //       plan
                    //     </>
                    //   ) : null
                    // }
                  />
                ),
                hideOnMobile: true,
              },
              { component: <Spacer width={4} />, hideOnTablet: true },
              {
                component: <ProductListButton productName="Canvas" />,
                hideOnTablet: true,
              },
              { component: <Spacer width={10} />, hideOnTablet: true },
              // TODO: Add this in later
              // {
              //   component: (
              //     <UpgradeCTA
              //       planName={account?.plan_info.name || "Free"}
              //       productName="Canvas"
              //       upgradeHref="https://zapier.com/free-trial/"
              //     />
              //   ),
              //   hideOnTablet: true,
              // },
            ]}
            centerColumnComponents={[
              {
                component: (
                  <InteractiveName
                    canvasName={canvas.name}
                    ownerId={canvas.customuser_id!}
                    permissions={isViewOnlyMode ? "view" : "edit"}
                  />
                ),
              },
            ]}
            rightColumnComponents={rightColumnComponents}
          />
        </Header>
        <Banner />
      </GridItem>
      <GridItem
        rowHeight={2}
        middle={true}
        overrideStyles={{
          flexFlow: "unset",
          zIndex: 999,
        }}
      >
        <SideBar>
          {toggles.manageProjectsMvp && !isPublic && (
            <ProjectSideBarItem
              handleSideBarItemPress={handleSideBarItemPress}
            />
          )}
          <SideBarItem
            name={SUMMARY}
            icon="fileGuide"
            tooltipTitle={capitalizeFirstLetter(SUMMARY)}
            selected={sideBarSelected === SUMMARY}
            onPress={() => handleSideBarItemPress(SUMMARY)}
          />
          <SideBarItem
            name="displayPreferences"
            icon="navCog"
            tooltipTitle="Display Preferences"
            selected={sideBarSelected === "displayPreferences"}
            onPress={() => handleSideBarItemPress("displayPreferences")}
          />
          {(session?.is_staff || session?.is_masquerade) && (
            <SideBarItem
              name="devtools"
              icon="actionBug"
              tooltipTitle="DevTools"
              selected={sideBarSelected === "devTools"}
              onPress={() => handleSideBarItemPress("devTools")}
            />
          )}
          {session?.is_staff && (
            <SideBarItem
              name="graphData"
              icon="navConnectedAssets"
              tooltipTitle="#wg-graph-opportunity"
              selected={sideBarSelected === "graphData"}
              onPress={() => handleSideBarItemPress("graphData")}
            />
          )}
          {toggles.aiCanvasComposer === true && (
            <SideBarItem
              name="composer"
              icon="commentBlank"
              tooltipTitle="Canvas Composer"
              selected={sideBarSelected === "composer"}
              onPress={() => handleSideBarItemPress("composer")}
            />
          )}
          {toggles.aiCanvasComposer === true && (
            <SideBarItem
              name="composer2"
              icon="commentBlank"
              tooltipTitle="Zapier Composer"
              selected={sideBarSelected === "composer2"}
              onPress={() => handleSideBarItemPress("composer2")}
            />
          )}
        </SideBar>
        <SidebarDrawer handleOnClose={handleOnClose} />
      </GridItem>
      <GridItem
        colWidth={2}
        rowHeight={2}
        overrideStyles={{
          gridColumnEnd: "auto",
        }}
      >
        {children}
        {((zslHasDraftNodes && !toggles.aiCanvasComposer) ||
          (zslHasDraftNodes && !isAiPage)) && <DraftBanner />}
      </GridItem>
      {showDiagrammingToolbar && (
        <Suspense fallback={null}>
          <DiagrammingToolbar />
        </Suspense>
      )}
      {showCanvasTemplateWidget && (
        <Suspense fallback={null}>
          <CreateCanvasFromTemplateToolbar />
        </Suspense>
      )}
      {(showWelcomeModal || showAIStreamCreateModal) && (
        <Suspense fallback={null}>
          <AIStreamCreateModal />
        </Suspense>
      )}
      {showTemplateFeedback && (
        <TemplateFeedback onClose={() => setShowTemplateFeedback(false)} />
      )}
    </Grid>
  );
}

function Banner() {
  const isViewer = useAtomValue(isViewerAtom);
  const hasZsl = useAtomValue(hasZslAtom);
  const zslHasDraftNodes = useAtomValue(zslHasDraftNodesAtom);
  const showCanvasTemplateWidget = useAtomValue(showCanvasTemplateWidgetAtom);
  const session = useAtomValue(sessionAtom);
  const isTemplatePage = useAtomValue(isTemplatePageAtom);
  const isModifyPromptOpen = useAtomValue(isModifyPromptOpenAtom);
  const viewOnlyDowntimeOverride = useAtomValue(isViewOnlyDowntimeOverrideAtom);
  const hasStreamingProgressStarted = useAtomValue(
    hasStreamingProgressStartedAtom,
  );
  if (viewOnlyDowntimeOverride) {
    return <ViewOnlyDowntimeBanner />;
  }
  if (hasZsl && zslHasDraftNodes) {
    if (!session?.is_logged_in) {
      return <LoggedOutTemplateBanner />;
    }

    if (showCanvasTemplateWidget) {
      return <TemplateBanner />;
    }

    if (!isViewer && !showCanvasTemplateWidget) {
      if (isTemplatePage) {
        return <TemplatePageBanner />;
      }

      if (!isModifyPromptOpen) {
        return (
          <>
            <AiCopilotBanner />
          </>
        );
      }
    }
  }

  if (hasStreamingProgressStarted) {
    return <ProgressBanner />;
  }

  return null;
}
